'use client';

import { createPortal } from 'react-dom';
import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import SearchHeader from '@/features/homeSearch/components/search-header.component';
import Spinner from '@/common/components/spinner/spinner.component';
import {
  SearchSuggestionsList,
  type SearchSuggestionsData,
} from './search-suggestions-list.component';

interface MobileSearchOverlayProps {
  value: string;
  isSearching: boolean;
  searchResults?: SearchSuggestionsData | null;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onSubmit: (text: string) => void;
  onClose: () => void;
  onNavigate?: () => void;
  footer?: ReactNode;
}

export function MobileSearchOverlay({
  value,
  isSearching,
  searchResults,
  onChange,
  onClear,
  onSubmit,
  onClose,
  onNavigate,
}: MobileSearchOverlayProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  if (!mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex flex-col bg-white lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="جستجو"
    >
      <div className="shrink-0 border-b border-plp-border/60 bg-white">
        <SearchHeader
          value={value}
          isSearching={isSearching}
          autoFocus
          clearSearch={onClear}
          handleSubmit={onSubmit}
          onChange={onChange}
          onBack={onClose}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        {isSearching ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <Spinner
              containerClassName="!w-auto !h-auto"
              spinnerClassName="!h-10 !w-10 !text-plp-accent"
            />
            <p className="text-sm font-medium text-plp-secondary">
              در حال جستجو...
            </p>
          </div>
        ) : (
          searchResults && (
            <SearchSuggestionsList
              value={value}
              data={searchResults}
              onNavigate={onNavigate}
            />
          )
        )}
      </div>
    </div>,
    document.body
  );
}
